import { FC } from 'react';
import {
  Box,
  Card,
  Checkbox,
  Collapse,
  Divider,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import type { ChoiceGroupQuestion, Cut, Extra, Trim } from '../../types';
import { formatPricing } from '../../utils/pricing';
import { getAllItems, getItemSpecifications } from './wizard-utils';
import { SpecFieldsGrid } from './spec-fields-grid';
import { useQuestionnaireResponse } from './questionnaire-response-context';

interface ChoiceGroupWizardProps {
  question: ChoiceGroupQuestion;
}

/**
 * Renders the interactive body for a ChoiceGroup question.
 * Each option is a radio card; selecting one expands to reveal the cuts it
 * contains, styled to match FreeSelection cards. When `allowSubSelection` is
 * true, each inner cut is individually toggleable via checkbox; otherwise every
 * cut is implicitly included and displayed for reference.
 * @param root0
 * @param root0.question
 */
export const ChoiceGroupWizard: FC<ChoiceGroupWizardProps> = ({ question }) => {
  const {
    catalog,
    answers,
    itemSpecValues,
    subSelections,
    handleAnswerChange,
    handleSpecChange,
    handleSubSelectionToggle,
    handleNext,
  } = useQuestionnaireResponse();
  const answer = (answers[question.id] as string) ?? '';
  const specValues = itemSpecValues[question.id] ?? {};
  const selectedSubCutIds = subSelections[question.id] ?? [];

  const allItems = getAllItems(catalog);

  const handleChoiceChange = (optionId: string) => {
    if (optionId === answer) return;
    handleAnswerChange(question.id, optionId);

    const selectedOption = question.options.find((o) => o.id === optionId);
    if (!selectedOption) return;

    // When sub-selection is on but the option has only one cut, there's
    // nothing to choose between — include it automatically so canonical state
    // matches the implied "Includes" UI.
    const singleCutAutoInclude =
      question.allowSubSelection && selectedOption.cutIds.length === 1;
    if (singleCutAutoInclude) {
      handleSubSelectionToggle(question.id, selectedOption.cutIds[0]);
    }

    // Real sub-selection (multiple cuts) keeps the user on this question.
    if (question.allowSubSelection && !singleCutAutoInclude) return;

    const optionItems = selectedOption.cutIds
      .map((cutId) => allItems.find((item) => item.id === cutId))
      .filter(Boolean) as (Cut | Trim | Extra)[];
    const hasSpecs = optionItems.some(
      (item) => getItemSpecifications(catalog, item.id).length > 0,
    );
    if (!hasSpecs) {
      setTimeout(() => handleNext(), 300);
    }
  };

  return (
    <Stack spacing={2}>
      {question.options.map((option) => {
        const isSelected = answer === option.id;

        const optionItems = option.cutIds
          .map((cutId) => allItems.find((item) => item.id === cutId))
          .filter(Boolean) as (Cut | Trim | Extra)[];

        const imageUrl = option.imageUrl || optionItems[0]?.imageUrl;
        // Only offer per-cut toggling when there's something to choose between.
        const subSelectable =
          question.allowSubSelection && optionItems.length > 1;

        return (
          <Card
            key={option.id}
            variant="outlined"
            sx={{
              borderColor: isSelected ? 'primary.main' : 'divider',
              borderWidth: 2,
              outline: '2px solid transparent',
              transition: 'all 0.3s ease',
              overflow: 'hidden',
              '&:hover': {
                outline: '2px solid',
                outlineColor: isSelected ? 'primary.main' : 'divider',
              },
            }}
          >
            {/* Header row — click to select this option */}
            <Stack
              direction="row"
              onClick={() => handleChoiceChange(option.id)}
              sx={{
                cursor: 'pointer',
                minHeight: imageUrl ? { xs: 120, md: 160 } : 80,
              }}
            >
              {imageUrl && (
                <Box
                  sx={{
                    width: { xs: 120, md: 160 },
                    minWidth: { xs: 120, md: 160 },
                    alignSelf: 'stretch',
                    position: 'relative',
                    bgcolor: 'primary.main',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    component="img"
                    src={imageUrl}
                    alt={option.label || ''}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
              )}

              <Stack
                sx={{ flex: 1, minWidth: 0, p: 2 }}
                justifyContent="center"
                spacing={0.5}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="h5">
                    {option.label || 'Unnamed'}
                  </Typography>
                  <Radio
                    checked={isSelected}
                    readOnly
                    sx={{ pointerEvents: 'none' }}
                  />
                </Stack>
                {option.description && (
                  <Typography variant="body2" color="text.secondary">
                    {option.description}
                  </Typography>
                )}
              </Stack>
            </Stack>

            {/* Expanded body — the cuts inside this option */}
            <Collapse in={isSelected} unmountOnExit>
              <Divider />
              {optionItems.length > 0 && (
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: 'block', px: 2, pt: 1.5 }}
                >
                  {subSelectable ? 'Choose which to include' : 'Includes'}
                </Typography>
              )}
              <Stack divider={<Divider sx={{ m: 1 }} />}>
                {optionItems.map((item) => {
                  const specs = getItemSpecifications(catalog, item.id);
                  const isIncluded = subSelectable
                    ? selectedSubCutIds.includes(item.id)
                    : true;

                  const handleCutClick = () => {
                    if (!subSelectable) return;
                    handleSubSelectionToggle(question.id, item.id);
                  };

                  return (
                    <Stack
                      key={item.id}
                      direction="row"
                      onClick={handleCutClick}
                      sx={{
                        cursor: subSelectable ? 'pointer' : 'default',
                        minHeight: item.imageUrl ? { xs: 120, md: 160 } : 64,
                      }}
                    >
                      {item.imageUrl && (
                        <Box
                          sx={{
                            width: { xs: 120, md: 160 },
                            minWidth: { xs: 120, md: 160 },
                            alignSelf: 'stretch',
                            position: 'relative',
                            bgcolor: 'primary.main',
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            component="img"
                            src={item.imageUrl}
                            alt={item.title || ''}
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        </Box>
                      )}

                      <Stack
                        sx={{ flex: 1, minWidth: 0, p: 2 }}
                        justifyContent="center"
                        spacing={0.5}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography variant="subtitle1">
                            {item.title || 'Unnamed'}
                          </Typography>
                          {subSelectable && (
                            <Checkbox
                              checked={isIncluded}
                              readOnly
                              sx={{ pointerEvents: 'none' }}
                            />
                          )}
                        </Stack>
                        {item.description && (
                          <Typography variant="body2" color="text.secondary">
                            {item.description}
                          </Typography>
                        )}
                        {item.pricing && (
                          <Typography
                            variant="caption"
                            color="primary"
                            sx={{ fontWeight: 'medium' }}
                          >
                            {formatPricing(item.pricing)}
                          </Typography>
                        )}
                        <Collapse
                          in={isIncluded && specs.length > 0}
                          unmountOnExit
                        >
                          <Divider sx={{ my: 1 }} />
                          <SpecFieldsGrid
                            specs={specs}
                            values={specValues[item.id] || {}}
                            onChange={(specId, v) =>
                              handleSpecChange(question.id, item.id, specId, v)
                            }
                          />
                        </Collapse>
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </Collapse>
          </Card>
        );
      })}
    </Stack>
  );
};
